/**
 * A tiny publish/subscribe channel for print jobs.
 *
 * Kept out of Redux on purpose. A receipt is transient: it is rendered, sent to
 * the printer, and thrown away. Putting it in application state would mean a
 * large blob of QR data sitting in the store, showing up in every devtools
 * snapshot, and surviving navigation for no reason.
 *
 *   import { printReceipt } from './printBus';
 *   await printReceipt(receiptModel, { copies: 2 });
 */

import { printCurrent } from '../../utils/print';

let listener = null;
let currentJob = null;

/** PrintRoot registers itself here. */
export const subscribe = (fn) => {
    listener = fn;
    return () => { if (listener === fn) listener = null; };
};

export const getCurrentJob = () => currentJob;

/**
 * Render a job into #print-root, print it, then clear it.
 *
 * The await between rendering and printing is what makes this work: the browser
 * has to have painted the receipt before the print starts, or an empty page
 * comes out. printCurrent waits two animation frames for exactly that reason.
 */
const run = async (job, printOptions) => {
    currentJob = job;
    listener?.(job);

    try {
        return await printCurrent(printOptions);
    } finally {
        // Clear afterwards so a stale receipt cannot be printed by a later
        // Ctrl+P, and so the hidden container is not holding a QR image.
        currentJob = null;
        listener?.(null);
    }
};

export const printReceipt = (receipt, { copies = 1, printerName = null } = {}) =>
    run({ type: 'receipt', data: receipt }, {
        copies,
        printerName,
        widthMM: receipt?.template?.paperWidthMM ?? 80
    });

export const printKitchenTicket = (ticket, { printerName = null } = {}) =>
    run({ type: 'kot', data: ticket }, {
        copies: 1,
        // Each kitchen section can have its own printer, so the ticket carries
        // the name it should go to.
        printerName: printerName || ticket?.printerName || null,
        widthMM: 80
    });

export const printShiftReport = (report, { printerName = null } = {}) =>
    run({ type: 'shift', data: report }, { copies: 1, printerName, widthMM: 80 });

/** Show a job on screen without printing - the preview on the orders screen. */
export const previewJob = (job) => {
    currentJob = job;
    listener?.(job);
};

export const clearJob = () => {
    currentJob = null;
    listener?.(null);
};
